import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { HEARTBEAT_INTERVAL_MS } from "score-common";
import { useScoreApi } from "../api/scoreApi";
import { syncEngine } from "../local/sync";
import { clearTheMachine, postDeferredReceipt, readDeferredReceipt } from "../local/clearMachine";
import { getPresenceInstanceId } from "../util/presenceInstanceId";

// #3518: the tablet's side of a Reset tablet asked for from Station Monitor.
//
// A SEPARATE poll from usePresenceHeartbeat on purpose (that file is not touched
// here): it runs whenever the tablet is signed in, with no gate on seat, show,
// visibility or inactivity, so a parked tablet still picks the command up.
//
// It acts on `pending` only. A remote clear is safe-only (never forced): if the
// tablet holds un-uploaded work the clear is blocked, reported to the server
// (the command becomes `blocked`) and shown in Station Monitor, NOT on the tablet:
// the scribe cannot act on it. A blocked command does NOT rerun by itself when the
// work drains; staff start it again from Station Monitor.
//
// Also posts a saved offline-download receipt (see local/clearMachine.js) once
// the NEW station is signed in and online.
export function useClearMachineCommand() {
  const auth = useAuth();
  const scoreApi = useScoreApi();
  const running = useRef(false);
  const isAuthenticated = Boolean(auth.isAuthenticated);
  const removeUser = auth.removeUser;

  const record = useCallback(
    (stationId, body) => scoreApi.stations.recordStationMachineClear(stationId, body),
    [scoreApi],
  );

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let disposed = false;
    const poll = async () => {
      if (disposed || running.current || !syncEngine.isOnline()) return;
      running.current = true;
      try {
        const stationId = getPresenceInstanceId();
        const reply = await scoreApi.stations.getStationMachineClear(stationId);
        const command = reply?.data?.data?.command;
        if (disposed || command?.status !== "pending") return;
        await clearTheMachine(
          {
            syncEngine,
            removeUser,
            api: {
              record,
              reportBlocked: (id, body) => scoreApi.stations.reportStationMachineClearBlocked(id, body),
            },
          },
          { via: "station-monitor", force: false },
        );
      } catch {
        // Silent, like the heartbeat: the next poll tries again.
      } finally {
        running.current = false;
      }
    };
    poll();
    const id = setInterval(poll, HEARTBEAT_INTERVAL_MS);
    return () => {
      disposed = true;
      clearInterval(id);
    };
  }, [isAuthenticated, scoreApi, removeUser, record]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let posting = false;
    const unsubscribe = syncEngine.subscribe(async (snapshot) => {
      if (!snapshot?.online || posting || !readDeferredReceipt()) return;
      posting = true;
      try {
        await postDeferredReceipt({ api: { record } });
      } finally {
        posting = false;
      }
    });
    return unsubscribe;
  }, [isAuthenticated, record]);

}

export default useClearMachineCommand;
